import { useCallback, useEffect } from 'react';
import {
  useReportDefinition,
  useReportFiltersManagement,
  useReportSectionSync,
  useReportExport,
} from '@reports/presentation/hooks';
import { ReportTypes } from '@reports/domain/types';
import {
  ReportBreadcrumb,
  ReportFiltersBar,
  ReportHeader,
  ReportSection,
  ReportTabs,
} from '@reports/presentation/components/base';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import type { ReportSection as ReportSectionType } from '@reports/domain/value-objects';

/**
 * Products Report Content
 *
 * Main content component for the products report.
 * Displays breadcrumb, header, filters bar, tabs, and sections.
 * Export toasts are handled automatically by the useReportExport hook.
 * Infrastructure dependencies are managed by composition root.
 */
export const ProductsReportContent = () => {
  const { getReportDefinition } = useReportDefinition();
  const productsReport = getReportDefinition(ReportTypes.PRODUCTS);

  const { appliedFilters, updateFilters, selectReport } = useReportFiltersManagement({
    autoLoad: true,
    autoSave: true,
  });

  const { activeSectionKey, handleSectionChange } = useReportSectionSync({
    sections: productsReport.sections,
  });

  const { exportReport, isLoading } = useReportExport();

  useEffect(() => {
    selectReport(productsReport);
  }, []);

  const handleFiltersChange = useCallback(
    (filters: BlockDataRequestDto) => {
      updateFilters(filters);
    },
    [updateFilters],
  );

  const handleExport = useCallback(() => {
    exportReport(appliedFilters, productsReport.export);
  }, [appliedFilters, exportReport, productsReport.export]);

  return (
    <div className="w-full p-6 font-sans">
      <ReportBreadcrumb items={[{ label: productsReport.metadata.name }]} />

      <ReportHeader report={productsReport} />

      <ReportFiltersBar
        appliedFilters={appliedFilters}
        isExporting={isLoading}
        onExport={handleExport}
        onFiltersChange={handleFiltersChange}
        reportDefinition={productsReport}
      />

      <ReportTabs
        activeSectionKey={activeSectionKey}
        onSectionChange={handleSectionChange}
        sections={productsReport.sections}
      >
        {(section: ReportSectionType) => <ReportSection request={appliedFilters} section={section} />}
      </ReportTabs>
    </div>
  );
};
